import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, Card } from '../../../design-system/components/core';
import { TextField, Select } from '../../../design-system/components/forms';
import MagicLoading from '../../../components/ui/MagicLoading';
import MagicFileUpload from '../../../components/ui/FormFields/MagicFileUpload';
import MagicAutocomplete from '../../../components/ui/FormFields/MagicAutocomplete';
import MagicAutocompleteVendor from '../../../components/ui/FormFields/MagicAutocompleteVendor';
import VendorDialog from '../../../components/vendors/VendorDialog';
import Breadcrumbs from '../../../components/core/Breadcrumbs';
import useExpenseStore from '../../../stores/expenseStore';
import useClientStore from '../../../stores/clientStore';
import useProjectStore from '../../../stores/projectStore';
import useAuthStore from '../../../stores/authStore';
import useConfirmationDialog from '../../../hooks/useConfirmationDialog';
import useUIStore from '../../../stores/uiStore';
import useVendorStore from '../../../stores/vendorStore';
import { api } from '../../../services/api';
import { getDynamicCurrencyOptions, getCurrencySymbolByCode } from '../../../utils/dynamicCurrencyUtils';
import SecureReceiptViewer from '../../../components/ui/SecureReceiptViewer';
import taxConfigService from '../../../services/taxConfigService';

const ExpenseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const {
    currentExpense,
    isLoading,
    fetchExpenseById,
    createExpense,
    updateExpense,
    uploadReceipt,
    deleteReceipt
  } = useExpenseStore();
  
  const { clients, fetchClients } = useClientStore();
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { addNotification } = useUIStore();
  const { searchVendors, fetchVendors, vendors } = useVendorStore();
  const [ConfirmDialog, confirm] = useConfirmationDialog();
  
  // Currency state
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  
  const [formData, setFormData] = useState({
    date: new Date(),
    amount: '',
    currency: 'USD',
    taxAmount: '',
    taxRate: '',
    taxConfigId: '', // Add tax configuration ID
    taxRegionCode: '', // Add tax region code
    category: '',
    vendor: '',
    vendorId: null,
    description: '',
    paymentMethod: '',
    billable: false,
    clientId: '',
    projectId: '',
    campaignId: '',
    purpose: '',
    notes: '',
    tags: []
  });
  
  const [receipts, setReceipts] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchProjects();
    fetchVendors(); // Load initial vendors
    
    if (isEditMode) {
      fetchExpenseById(id);
    }
  }, [id, isEditMode, fetchExpenseById, fetchClients, fetchProjects, fetchVendors]);

  // Load currencies dynamically
  useEffect(() => {
    const loadCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const currencyOptions = await getDynamicCurrencyOptions();
        setCurrencies(currencyOptions);
      } catch (error) {
        console.error('Failed to load currencies:', error);
        setCurrencies([]);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    
    loadCurrencies();
  }, []);

  // Update currency symbol when currency changes
  useEffect(() => {
    const updateCurrencySymbol = async () => {
      try {
        const symbol = await getCurrencySymbolByCode(formData.currency);
        setCurrencySymbol(symbol);
      } catch (error) {
        console.error('Failed to get currency symbol:', error);
        setCurrencySymbol('$'); // fallback
      }
    };
    
    if (formData.currency) {
      updateCurrencySymbol();
    }
  }, [formData.currency]);

  useEffect(() => {
    if (currentExpense && isEditMode) {
      setFormData({
        date: new Date(currentExpense.date),
        amount: currentExpense.amount,
        currency: currentExpense.currency,
        taxAmount: currentExpense.taxAmount || '',
        taxRate: currentExpense.taxRate || '',
        category: currentExpense.category,
        vendor: currentExpense.vendor || '',
        vendorId: currentExpense.vendorId?._id || currentExpense.vendorId || '',
        description: currentExpense.description,
        paymentMethod: currentExpense.paymentMethod,
        billable: currentExpense.billable,
        clientId: currentExpense.clientId?._id || '',
        projectId: currentExpense.projectId?._id || '',
        campaignId: currentExpense.campaignId?._id || '',
        purpose: currentExpense.purpose || '',
        notes: currentExpense.notes || '',
        tags: currentExpense.tags || []
      });
      
      // If expense has a vendor, set it as selected
      if (currentExpense.vendorId) {
        const vendor = typeof currentExpense.vendorId === 'object' 
          ? currentExpense.vendorId 
          : { _id: currentExpense.vendorId, name: currentExpense.vendor };
        
        setSelectedVendor(vendor);
        setVendorOptions(prev => {
          const exists = prev.some(v => v._id === vendor._id);
          return exists ? prev : [vendor, ...prev];
        });
      }
      
      // Don't set existing receipts in the receipts state
      // They will be displayed separately and receipts state is only for new uploads
    }
  }, [currentExpense, isEditMode]);

  const breadcrumbs = [
    { label: 'Finances', path: '/finances' },
    { label: 'Expenses', path: '/finances/expenses' },
    { label: isEditMode ? 'Edit Expense' : 'Create Expense' }
  ];

  const categoryOptions = [
    { value: 'travel', label: 'Travel' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'meals', label: 'Meals' },
    { value: 'supplies', label: 'Supplies' },
    { value: 'software', label: 'Software' },
    { value: 'hardware', label: 'Hardware' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'rent', label: 'Rent' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'professional_services', label: 'Professional Services' },
    { value: 'payroll', label: 'Payroll' },
    { value: 'taxes', label: 'Taxes' },
    { value: 'other', label: 'Other' }
  ];

  const paymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'check', label: 'Check' },
    { value: 'company_card', label: 'Company Card' },
    { value: 'personal_card', label: 'Personal Card' },
    { value: 'petty_cash', label: 'Petty Cash' },
    { value: 'digital_wallet', label: 'Digital Wallet' },
    { value: 'other', label: 'Other' }
  ];

    // Dynamic tax rate options that will be populated from the server
  const [taxRateOptions, setTaxRateOptions] = useState([
    { value: 0, label: '0%' } // Default option
  ]);
  
  // Fetch tax rates from the server
  const [taxRegion, setTaxRegion] = useState(null);
  // Track tax loading state
  const [taxLoadingState, setTaxLoadingState] = useState({
    loading: true,
    error: null
  });
  
  // Tax configurations state
  const [taxConfigurations, setTaxConfigurations] = useState([]);
  const [loadingTaxConfigs, setLoadingTaxConfigs] = useState(false);
  
  // Load tax configurations
  useEffect(() => {
    const loadTaxConfigurations = async () => {
      try {
        setLoadingTaxConfigs(true);
        console.log('[ExpenseForm] Loading tax configurations...');
        
        // Load all active tax configurations
        console.log('[ExpenseForm] Calling getAllTaxConfigs with params:', { isTemplate: false });
        const response = await taxConfigService.getAllTaxConfigs({ isTemplate: false });
        console.log('[ExpenseForm] Tax config response:', response);
        
        // Handle both wrapped and unwrapped responses
        let configs = [];
        if (Array.isArray(response)) {
          configs = response;
        } else if (response?.data && Array.isArray(response.data)) {
          configs = response.data;
        } else if (response?.configs && Array.isArray(response.configs)) {
          configs = response.configs;
        }
        
        console.log('[ExpenseForm] Extracted configs:', configs);
        setTaxConfigurations(configs);
        console.log('[ExpenseForm] Loaded tax configurations:', configs.length, configs);
        
        // Set default tax config if available
        if (configs.length > 0 && !formData.taxConfigId) {
          // Try to find DEFAULT or EG config
          const defaultConfig = configs.find(c => c.regionCode === 'DEFAULT') || 
                              configs.find(c => c.regionCode === 'EG') || 
                              configs[0];
          if (defaultConfig) {
            setFormData(prev => ({
              ...prev,
              taxConfigId: defaultConfig._id,
              taxRegionCode: defaultConfig.regionCode
            }));
          }
        }
      } catch (error) {
        console.error('[ExpenseForm] Error loading tax configurations:', error);
        addNotification({
          message: 'Failed to load tax configurations',
          severity: 'error'
        });
      } finally {
        setLoadingTaxConfigs(false);
      }
    };
    
    loadTaxConfigurations();
  }, []);
  
  // Load tax rates when tax configuration changes
  useEffect(() => {
    const loadTaxRates = async () => {
      if (!formData.taxConfigId) {
        setTaxRateOptions([{ value: 0, label: '0% - No Tax' }]);
        return;
      }
      
      try {
        setTaxLoadingState({ loading: true, error: null });
        console.log('[ExpenseForm] Loading tax rates for config:', formData.taxConfigId);
        
        // Get the selected tax configuration
        const selectedConfig = taxConfigurations.find(c => c._id === formData.taxConfigId);
        if (!selectedConfig) return;
        
        // Use the selected tax configuration
        setTaxRegion(selectedConfig);
        
        if (selectedConfig.taxTypes && selectedConfig.taxTypes.length > 0) {
          // Convert tax types to options
          const options = selectedConfig.taxTypes
            .filter(type => type.isActive && type.calculationMethod === 'percentage')
            .map(type => ({
              value: type.rate,
              label: `${type.rate}% - ${type.name}`,
              code: type.code,
              name: type.name,
              isDefault: type.isDefault
            }));
          
          console.log('[ExpenseForm] Tax rate options:', options);
          
          // Add 0% option if not present
          if (!options.some(opt => opt.value === 0)) {
            options.unshift({ value: 0, label: '0% - No Tax', code: 'NONE', name: 'No Tax' });
          }
          
          setTaxRateOptions(options);
          setTaxLoadingState({ loading: false, error: null });
          
          // If editing and tax rate is set, keep it
          // Otherwise set to default tax rate
          if (!isEditMode || !formData.taxRate) {
            const defaultTax = options.find(opt => opt.isDefault) || options[0];
            console.log('[ExpenseForm] Using default tax rate:', defaultTax);
            
            setFormData(prev => ({
              ...prev,
              taxRate: defaultTax.value.toString()
            }));
          } else {
            console.log('[ExpenseForm] Using existing tax rate:', formData.taxRate);
          }
        } else {
          // No tax types configured
          console.log('[ExpenseForm] No tax types found in configuration');
          setTaxRateOptions([{ value: 0, label: '0% - No Tax' }]);
          setTaxLoadingState({ loading: false, error: null });
          
          if (!isEditMode && !formData.taxRate) {
            setFormData(prev => ({
              ...prev,
              taxRate: '0'
            }));
          }
        }
      } catch (error) {
        console.error('Error loading tax rates:', error);
        setTaxLoadingState({ 
          loading: false, 
          error: 'Failed to load tax configuration.' 
        });
        
        // Fall back to no tax
        setTaxRateOptions([{ value: 0, label: '0% - No Tax' }]);
        if (!formData.taxRate) {
          setFormData(prev => ({
            ...prev,
            taxRate: '0'
          }));
        }
      }
    };
    
    loadTaxRates();
  }, [formData.taxConfigId, taxConfigurations, isEditMode]);

  // Simple vendor search with debounce
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      console.log('[ExpenseForm] Vendor search triggered, query:', vendorSearchQuery);
      
      if (vendorSearchQuery.length >= 1) {
        try {
          console.log('[ExpenseForm] Searching vendors...');
          const results = await searchVendors(vendorSearchQuery);
          console.log('[ExpenseForm] Search results:', results);
          
          // Only show "Create new" if there are no results
          if (!results || results.length === 0) {
            setVendorOptions([{ 
              _id: 'create-new', 
              name: `Create "${vendorSearchQuery}"`, 
              searchQuery: vendorSearchQuery,
              isCreateNew: true 
            }]);
          } else {
            setVendorOptions(results);
          }
        } catch (error) {
          console.error('[ExpenseForm] Search error:', error);
          // On error, show create new option
          setVendorOptions([{ 
            _id: 'create-new', 
            name: `Create "${vendorSearchQuery}"`, 
            searchQuery: vendorSearchQuery,
            isCreateNew: true 
          }]);
        }
      } else {
        // Empty search, show all vendors
        console.log('[ExpenseForm] Empty query, showing all vendors:', vendors);
        setVendorOptions(vendors || []);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [vendorSearchQuery, searchVendors, vendors]);

  const handleChange = async (field, value) => {
    console.log(`[ExpenseForm] Field changed: ${field} = ${value}`);
    
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Calculate tax amount when rate or amount changes
      if ((field === 'taxRate' || field === 'amount') && newData.amount && newData.taxRate !== '') {
        try {
          console.log(`[ExpenseForm] Calculating tax for amount: ${newData.amount}, rate: ${newData.taxRate}%`);
          
          // For immediate feedback, do a client-side calculation
          const calculatedTaxAmount = (parseFloat(newData.amount) * (parseFloat(newData.taxRate) / 100)).toFixed(2);
          console.log(`[ExpenseForm] Client-side calculated tax: ${calculatedTaxAmount}`);
          newData.taxAmount = calculatedTaxAmount;
          
          // If we have a tax region, also trigger a server-side calculation
          if (taxRegion && taxRegion._id) {
            // This runs in the background, will update when complete
            (async () => {
              try {
                const selectedRate = taxRateOptions.find(opt => opt.value.toString() === newData.taxRate.toString());
                const taxTypeCode = selectedRate?.code;
                
                console.log(`[ExpenseForm] Selected tax rate:`, selectedRate);
                
                if (taxTypeCode) {
                  console.log(`[ExpenseForm] Requesting server-side tax calculation with:`, {
                    configId: taxRegion._id,
                    amount: parseFloat(newData.amount),
                    taxTypeCode,
                    includeInPrice: false
                  });
                  
                  const result = await api.calculateTax(taxRegion._id, {
                    amount: parseFloat(newData.amount),
                    taxTypeCode,
                    includeInPrice: false
                  });
                  
                  console.log(`[ExpenseForm] Server-side tax calculation result:`, result);
                  
                  if (result && result.taxAmount !== undefined) {
                    // Update with the server-calculated amount
                    console.log(`[ExpenseForm] Updating tax amount to server-calculated: ${result.taxAmount}`);
                    setFormData(prev => ({
                      ...prev,
                      taxAmount: result.taxAmount.toFixed(2)
                    }));
                  } else {
                    console.log(`[ExpenseForm] Server-side calculation returned no taxAmount, keeping client-side calculation`);
                  }
                } else {
                  console.log(`[ExpenseForm] No tax type code found for rate ${newData.taxRate}, using client-side calculation`);
                }
              } catch (error) {
                console.error('Error calculating tax:', error);
                console.log('[ExpenseForm] Using client-side calculation as fallback');
                // Client-side calculation already done as fallback
              }
            })();
          } else {
            console.log('[ExpenseForm] No tax region available, using client-side calculation only');
          }
        } catch (error) {
          console.error('Error in tax calculation:', error);
        }
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileUpload = (files) => {
    console.log('[ExpenseForm] handleFileUpload called with:', files);
    setReceipts(files);
  };
  
  const handleDeleteExistingReceipt = async (receipt) => {
    console.log('[ExpenseForm] Deleting receipt:', receipt);
    
    const confirmed = await confirm({
      title: 'Delete Receipt',
      content: 'Are you sure you want to delete this receipt?',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteReceipt(currentExpense._id, receipt._id);
        // Re-fetch expense to update the list
        await fetchExpenseById(id);
        addNotification({
          message: 'Receipt deleted successfully',
          type: 'success'
        });
      } catch (error) {
        console.error('[ExpenseForm] Error deleting receipt:', error);
        addNotification({
          message: 'Failed to delete receipt',
          type: 'error'
        });
      }
    }
  };

  const handleVendorSelect = (event, value) => {
    console.log('[ExpenseForm] Vendor selected:', value);
    
    if (value && value.isCreateNew) {
      // Open dialog to create new vendor
      const searchText = value.searchQuery || vendorSearchQuery;
      console.log('[ExpenseForm] Opening vendor dialog for:', searchText);
      setVendorSearchQuery(searchText);
      setShowVendorDialog(true);
    } else if (value) {
      // Selected existing vendor
      setSelectedVendor(value);
      setFormData(prev => ({
        ...prev,
        vendorId: value._id,
        vendor: value.name
      }));
    } else {
      // Cleared selection
      setSelectedVendor(null);
      setFormData(prev => ({
        ...prev,
        vendorId: null,
        vendor: ''
      }));
      setVendorSearchQuery('');
      setVendorOptions(vendors || []);
    }
  };

  const handleVendorCreated = (vendor) => {
    console.log('handleVendorCreated called with vendor:', vendor);
    
    if (!vendor) {
      console.error('No vendor provided to handleVendorCreated');
      addNotification({
        message: 'Vendor created but data was not received properly',
        type: 'warning'
      });
      setShowVendorDialog(false);
      return;
    }
    
    // Set the newly created vendor
    setSelectedVendor(vendor);
    setFormData(prev => ({
      ...prev,
      vendorId: vendor._id,
      vendor: vendor.name
    }));
    setVendorOptions(prev => [vendor, ...prev]);
    setVendorSearchQuery('');
    setShowVendorDialog(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.amount) newErrors.amount = 'Amount is required';
    if (formData.amount && formData.amount <= 0) newErrors.amount = 'Amount must be greater than 0';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.paymentMethod) newErrors.paymentMethod = 'Payment method is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      addNotification({
        message: 'Please fix the errors in the form',
        type: 'error'
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      let savedExpense;
      
      // Check if we have receipts to upload with new expense creation
      if (receipts.length > 0 && !isEditMode) {
        // Use FormData for creating expense with receipts
        const formDataObj = new FormData();
        
        console.log('[ExpenseForm] Creating expense with receipts');
        console.log('[ExpenseForm] Form data:', formData);
        console.log('[ExpenseForm] Receipts:', receipts);
        
        // Add expense data fields to FormData
        formDataObj.append('amount', parseFloat(formData.amount).toString());
        // Convert date to ISO string, handling both Date objects and strings
        const dateValue = formData.date instanceof Date ? formData.date.toISOString() : formData.date;
        formDataObj.append('date', dateValue);
        formDataObj.append('category', formData.category);
        formDataObj.append('paymentMethod', formData.paymentMethod);
        formDataObj.append('currency', formData.currency);
        formDataObj.append('billable', formData.billable.toString()); // Convert boolean to string
        
        if (formData.taxAmount) formDataObj.append('taxAmount', parseFloat(formData.taxAmount).toString());
        if (formData.taxRate) formDataObj.append('taxRate', parseFloat(formData.taxRate).toString());
        if (formData.taxConfigId) formDataObj.append('taxConfigId', formData.taxConfigId);
        if (formData.taxRegionCode) formDataObj.append('taxRegionCode', formData.taxRegionCode);
        if (formData.vendor) formDataObj.append('vendor', formData.vendor);
        if (formData.vendorId) formDataObj.append('vendorId', formData.vendorId);
        if (formData.description) formDataObj.append('description', formData.description);
        if (formData.clientId) formDataObj.append('clientId', formData.clientId);
        if (formData.projectId) formDataObj.append('projectId', formData.projectId);
        if (formData.campaignId) formDataObj.append('campaignId', formData.campaignId);
        if (formData.purpose) formDataObj.append('purpose', formData.purpose);
        if (formData.notes) formDataObj.append('notes', formData.notes);
        if (formData.tags) formDataObj.append('tags', JSON.stringify(formData.tags));
        
        formDataObj.append('owner', user._id);
        
        // Add receipts to FormData
        receipts.forEach((receipt, index) => {
          if (receipt instanceof File) {
            formDataObj.append('receipts', receipt);
            console.log(`[ExpenseForm] Added receipt ${index}:`, receipt.name, receipt.size);
          }
        });
        
        // Log FormData contents for debugging
        console.log('[ExpenseForm] FormData contents:');
        for (let [key, value] of formDataObj.entries()) {
          console.log(`  ${key}:`, value);
        }
        
        savedExpense = await createExpense(formDataObj);
        addNotification({
          message: 'Expense created successfully',
          type: 'success'
        });
      } else {
        // Use regular JSON for editing or creating without receipts
        const expenseData = {
          ...formData,
          amount: parseFloat(formData.amount),
          taxAmount: formData.taxAmount ? parseFloat(formData.taxAmount) : 0,
          taxRate: formData.taxRate ? parseFloat(formData.taxRate) : 0,
          vendor: formData.vendor || '',
          vendorId: formData.vendorId,
          owner: user._id
        };
        
        // Remove empty fields
        Object.keys(expenseData).forEach(key => {
          if (expenseData[key] === '') {
            delete expenseData[key];
          }
        });
        
        if (isEditMode) {
          savedExpense = await updateExpense(id, expenseData);
          addNotification({
            message: 'Expense updated successfully',
            type: 'success'
          });
        } else {
          savedExpense = await createExpense(expenseData);
          addNotification({
            message: 'Expense created successfully',
            type: 'success'
          });
        }
        
        // Upload receipts separately if editing
        if (isEditMode && receipts.length > 0) {
          for (const receipt of receipts) {
            if (receipt instanceof File) {
              try {
                await uploadReceipt(savedExpense._id, receipt);
              } catch (error) {
                console.error('Error uploading receipt:', error);
                addNotification({
                  message: 'Some receipts failed to upload',
                  type: 'warning'
                });
              }
            }
          }
        }
      }
      
      navigate(`/finances/expenses/${savedExpense._id}`);
    } catch (error) {
      addNotification({
        message: `Failed to ${isEditMode ? 'update' : 'create'} expense`,
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = async () => {
    const hasChanges = isEditMode ? 
      JSON.stringify(formData) !== JSON.stringify({
        date: new Date(currentExpense.date),
        amount: currentExpense.amount,
        currency: currentExpense.currency,
        taxAmount: currentExpense.taxAmount || '',
        taxRate: currentExpense.taxRate || '',
        category: currentExpense.category,
        vendor: currentExpense.vendor || '',
        description: currentExpense.description,
        paymentMethod: currentExpense.paymentMethod,
        billable: currentExpense.billable,
        clientId: currentExpense.clientId?._id || '',
        projectId: currentExpense.projectId?._id || '',
        campaignId: currentExpense.campaignId?._id || '',
        purpose: currentExpense.purpose || '',
        notes: currentExpense.notes || '',
        tags: currentExpense.tags || []
      }) : 
      Object.values(formData).some(value => value !== '' && value !== false);
    
    if (hasChanges) {
      const confirmed = await confirm({
        title: 'Discard Changes',
        content: 'Are you sure you want to discard your changes?'
      });
      
      if (confirmed) {
        navigate('/finances/expenses');
      }
    } else {
      navigate('/finances/expenses');
    }
  };

  if (isLoading && isEditMode) {
    return <MagicLoading />;
  }

  return (
    <Box>
      <Breadcrumbs items={breadcrumbs} />
      
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/finances/expenses')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4">
            {isEditMode ? 'Edit Expense' : 'Create New Expense'}
          </Typography>
        </Box>
      </Box>

      {/* Alert for edit mode */}
      {isEditMode && currentExpense?.status !== 'draft' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Only draft expenses can be edited. Some fields may be restricted.
        </Alert>
      )}

      {/* Form */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Date"
                    value={formData.date}
                    onChange={(value) => handleChange('date', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!errors.date}
                        helperText={errors.date}
                        fullWidth
                        required
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Amount"
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                    type="number"
                    error={!!errors.amount}
                    helperText={errors.amount}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
                    }}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Select
                    label="Currency"
                    value={formData.currency}
                    onChange={(value) => handleChange('currency', value)}
                    error={!!errors.currency}
                    helperText={errors.currency}
                    options={currencies}
                    loading={loadingCurrencies}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Select
                    label="Tax Configuration"
                    value={formData.taxConfigId}
                    onChange={(value) => {
                      const selectedConfig = taxConfigurations.find(c => c._id === value);
                      handleChange('taxConfigId', value);
                      handleChange('taxRegionCode', selectedConfig?.regionCode || '');
                      // Reset tax rate when configuration changes
                      handleChange('taxRate', '');
                    }}
                    options={[
                      { value: '', label: 'No Tax Configuration' },
                      ...taxConfigurations.map(config => ({
                        value: config._id,
                        label: `${config.regionName} (${config.countryName})`
                      }))
                    ]}
                    loading={loadingTaxConfigs}
                    helperText={loadingTaxConfigs ? 'Loading configurations...' : ''}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Select
                    label="Tax Rate"
                    value={formData.taxRate}
                    onChange={(value) => handleChange('taxRate', value)}
                    options={taxRateOptions}
                    loading={taxLoadingState.loading}
                    error={!!taxLoadingState.error}
                    helperText={
                      taxLoadingState.loading 
                        ? 'Loading tax rates...' 
                        : taxLoadingState.error
                          ? taxLoadingState.error
                          : taxRegion
                            ? `Tax region: ${taxRegion.regionName || taxRegion.regionCode}`
                            : formData.taxConfigId
                              ? 'Select a tax rate'
                              : 'Select a tax configuration first'
                    }
                    disabled={!formData.taxConfigId}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Select
                    label="Category"
                    value={formData.category}
                    onChange={(value) => handleChange('category', value)}
                    options={categoryOptions}
                    error={!!errors.category}
                    helperText={errors.category}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Select
                    label="Payment Method"
                    value={formData.paymentMethod}
                    onChange={(value) => handleChange('paymentMethod', value)}
                    options={paymentMethodOptions}
                    error={!!errors.paymentMethod}
                    helperText={errors.paymentMethod}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Tax Amount"
                    value={formData.taxAmount}
                    type="number"
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
                    }}
                    helperText="Auto-calculated based on tax rate"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <MagicAutocompleteVendor
                    value={selectedVendor}
                    onChange={(event, value) => handleVendorSelect(event, value)}
                    inputValue={vendorSearchQuery}
                    onInputChange={(event, value, reason) => {
                      console.log('[ExpenseForm] Input change:', value, reason);
                      // Always update search query for input changes
                      setVendorSearchQuery(value || '');
                      
                      // Clear selection only when explicitly cleared
                      if (reason === 'clear') {
                        setSelectedVendor(null);
                      }
                    }}
                    options={vendorOptions}
                    loading={false}
                    error={!!errors.vendor}
                    helperText={errors.vendor || "Search vendors or create new"}
                    placeholder="Search for vendor..."
                    freeSolo={false}
                    clearOnBlur={false}
                    disableCloseOnSelect={false}
                    openOnFocus={true}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description}
                    placeholder="Describe this expense (optional)"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Purpose"
                    value={formData.purpose}
                    onChange={(e) => handleChange('purpose', e.target.value)}
                    multiline
                    rows={2}
                    placeholder="Business purpose of this expense"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Additional notes or comments"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.billable}
                        onChange={(e) => handleChange('billable', e.target.checked)}
                      />
                    }
                    label="This expense is billable to a client"
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Card>

          {/* Associations */}
          <Card sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Associations
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Select
                  label="Client"
                  name="clientId"
                  value={formData.clientId}
                  onChange={(name, value) => handleChange(name, value)}
                  options={[
                    { value: '', label: 'None' },
                    ...clients.map(client => ({
                      value: client._id,
                      label: client.name
                    }))
                  ]}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Select
                  label="Project"
                  value={formData.projectId}
                  onChange={(value) => handleChange('projectId', value)}
                  options={[
                    { value: '', label: 'None' },
                    ...projects
                      .filter(p => !formData.clientId || p.clientId === formData.clientId)
                      .map(project => ({
                        value: project._id,
                        label: project.name
                      }))
                  ]}
                  disabled={!formData.clientId}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Select
                  label="Campaign"
                  value={formData.campaignId}
                  onChange={(value) => handleChange('campaignId', value)}
                  options={[
                    { value: '', label: 'None' }
                    // Add campaigns when available
                  ]}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          {/* Receipts */}
          <Card>
            <Typography variant="h6" gutterBottom>
              Receipts
            </Typography>
            
            {/* Show existing receipts in edit mode */}
            {isEditMode && currentExpense?.receipts && currentExpense.receipts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Receipts
                </Typography>
                <SecureReceiptViewer
                  receipts={currentExpense.receipts}
                  onDelete={handleDeleteExistingReceipt}
                  showDelete={true}
                />
              </Box>
            )}
            
            {/* File upload for new receipts */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {isEditMode && currentExpense?.receipts?.length > 0 ? 'Add More Receipts' : 'Upload Receipts'}
              </Typography>
              <MagicFileUpload
                value={receipts}
                onChange={handleFileUpload}
                accept="image/*,.pdf"
                multiple
                maxFiles={5}
                maxSize={5 * 1024 * 1024} // 5MB
                icon={<AttachFileIcon />}
                helperText="Upload receipts (images or PDF, max 5MB each)"
              />
            </Box>
          </Card>

          {/* Actions */}
          <Card sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                loading={isSaving}
                fullWidth
              >
                {isEditMode ? 'Update Expense' : 'Create Expense'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                fullWidth
              >
                Cancel
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <ConfirmDialog />
      
      <VendorDialog
        open={showVendorDialog}
        onClose={() => setShowVendorDialog(false)}
        onVendorCreated={handleVendorCreated}
        initialName={vendorSearchQuery}
        key={showVendorDialog ? 'open' : 'closed'}
      />
    </Box>
  );
};

export default ExpenseForm;